import { useState } from 'react';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { fileApi, documentApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface FileUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface UploadedFile {
  file: File;
  id: string;
}

export function FileUploadModal({ open, onOpenChange }: FileUploadModalProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [resourceNames, setResourceNames] = useState<string[]>([]);
  const [resourceIds, setResourceIds] = useState<string[]>([]);
  const [showUploadError, setShowUploadError] = useState(false);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { addNotification } = useNotificationStore();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files).map((file) => ({
        file,
        id: `${file.name}-${Date.now()}`,
      }));
      setFiles(selectedFiles);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).map((file) => ({
      file,
      id: `${file.name}-${Date.now()}`,
    }));
    setFiles(droppedFiles);
  };

  const removeFile = (idToRemove: string) => {
    setFiles(files.filter((f) => f.id !== idToRemove));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      addNotification({
        type: 'error',
        title: 'No Files Selected',
        message: 'Please select at least one file before uploading',
      });
      return;
    }

    // Reset error states
    setShowUploadError(false);
    setUpgradeRequired(false);
    setErrorMessage('');
    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach(({ file }) => {
        formData.append('files', file);
      });

      const response = await fileApi.uploadFiles(formData);

      if (response === 'error') {
        setShowUploadError(true);
        setUploading(false);
        return;
      }

      setJobIds(response.job_ids || []);
      setResourceNames(response.resource_names || []);
      setResourceIds(response.resource_ids || []);

      // Manually update session_context to ensure uploads appear in Active Documents immediately
      if (response.resource_names && response.resource_ids) {
        const storedContext = localStorage.getItem('session_context');
        const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};

        // Initialize arrays if they don't exist
        context.resource_names = context.resource_names || [];
        context.resource_ids = context.resource_ids || [];

        // Add new uploads to context
        response.resource_names.forEach((name: string, index: number) => {
          if (!context.resource_names.includes(name)) {
            context.resource_names.push(name);
            context.resource_ids.push(response.resource_ids[index]);
          }
        });

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }

      // Add files to loading state
      response.resource_ids?.forEach((id: string, index: number) => {
        addLoadingDocument(id, 'Upload', response.resource_names[index]);
      });

      // Start polling for each job
      response.job_ids?.forEach((jobId: string, index: number) => {
        pollJobStatus(jobId, response.resource_names[index], response.resource_ids[index]);
      });

      addNotification({
        type: 'info',
        title: 'Files Uploading',
        message: `Processing ${files.length} file${files.length !== 1 ? 's' : ''}...`,
        duration: 60000,
      });

      // Clear files after upload
      setFiles([]);
    } catch (error: any) {
      console.error(error);
      if (error.response && error.response.status === 402) {
        setUpgradeRequired(true);
        setErrorMessage(
          error.response.data?.error ||
            'File upload requires a pro or premium subscription. Please upgrade your account to upload files.'
        );
      } else {
        setShowUploadError(true);
        addNotification({
          type: 'error',
          title: 'Upload Failed',
          message: 'Failed to upload files. Please try again.',
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const pollJobStatus = (jobId: string, resourceName: string, resourceId: string) => {
    // Check if already complete
    if (jobId.toLowerCase().startsWith('complete') || jobId.toLowerCase().startsWith('exists')) {
      removeLoadingDocument(resourceId, 'Upload');
      addNotification({
        type: 'success',
        title: 'File Ready',
        message: `${resourceName} has been loaded and added to your session.`,
      });
      return;
    }

    let pollCount = 0;
    const maxPolls = 60;

    const poll = async () => {
      pollCount++;

      try {
        const response = await documentApi.getJobStatus(jobId);

        if (response?.status) {
          const status = response.status.toLowerCase();

          if (status.startsWith('complete')) {
            // Success
            removeLoadingDocument(resourceId, 'Upload');
            addNotification({
              type: 'success',
              title: 'File Ready',
              message: `${resourceName} has been loaded and added to your session.`,
            });
            return;
          } else if (status.startsWith('error')) {
            // Error
            removeLoadingDocument(resourceId, 'Upload');
            addNotification({
              type: 'error',
              title: 'File Processing Failed',
              message: `Failed to process ${resourceName}. Please try again.`,
              duration: 8000,
            });

            // Remove from context
            const stored = localStorage.getItem('session_context');
            const context = stored && stored !== 'undefined' ? JSON.parse(stored) : {};

            if (context.resource_ids) {
              context.resource_ids = (context.resource_ids || []).filter(
                (id: string) => id !== resourceId
              );
            }
            if (context.resource_names) {
              context.resource_names = (context.resource_names || []).filter(
                (name: string) => name !== resourceName
              );
            }

            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));
            return;
          }

          // Still processing, continue polling
          if (pollCount < maxPolls) {
            const nextInterval = pollCount < 10 ? 3000 : pollCount < 20 ? 5000 : 10000;
            setTimeout(poll, nextInterval);
          } else {
            // Max polls reached
            removeLoadingDocument(resourceId, 'Upload');
            addNotification({
              type: 'error',
              title: 'Processing Timeout',
              message: `Processing ${resourceName} took too long. Please check back later.`,
              duration: 8000,
            });
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        if (pollCount < maxPolls) {
          setTimeout(poll, 5000);
        }
      }
    };

    poll();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-900">Upload Documents</DialogTitle>
          <DialogDescription className="text-gray-600">
            Upload PDF, DOC, DOCX, or TXT files to add to your session
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drag and Drop Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
              isDragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            }`}
          >
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept=".pdf,.doc,.docx,.txt"
            />

            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gray-200">
                <Upload className="text-2xl text-gray-600" />
              </div>

              <div>
                <p className="text-lg font-medium text-gray-700">
                  {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
                </p>
                <p className="text-sm mt-1 text-gray-500">
                  or{' '}
                  <label
                    htmlFor="file-upload"
                    className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                  >
                    browse to upload
                  </label>
                </p>
              </div>

              <div className="text-xs text-gray-400">Supports PDF, DOC, DOCX, TXT files</div>
            </div>
          </div>

          {/* Selected Files List */}
          {files.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Selected Files ({files.length})
              </h3>
              <div className="space-y-2">
                {files.map(({ file, id }) => (
                  <div
                    key={id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <FileText className="flex-shrink-0 text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(id)}
                      className="flex-shrink-0 p-1 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleSubmit}
                disabled={files.length === 0 || uploading}
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {uploading
                  ? 'Uploading...'
                  : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}

          {/* Error Messages */}
          {showUploadError && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  Upload failed. If the file is a PDF, try using OCR first or ensure it's not
                  password-protected.
                </div>
              </div>
            </div>
          )}

          {upgradeRequired && (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <div className="font-semibold text-yellow-800 mb-2">Upgrade Required</div>
              <div className="text-yellow-700 mb-3 text-sm">{errorMessage}</div>
              <Button
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                onClick={() => {
                  onOpenChange(false);
                  // Navigate to account/upgrade page
                  window.location.href = '/profile';
                }}
              >
                Upgrade Now
              </Button>
            </div>
          )}

          {/* Processing Status */}
          {jobIds.length > 0 && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="text-sm text-blue-700 font-medium mb-2">Processing Files...</div>
              <div className="text-xs text-blue-600">
                {resourceNames.length} file{resourceNames.length !== 1 ? 's' : ''} being processed.
                You'll be notified when they're ready.
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
